import Layout from 'components/layout';

import React from 'react';

import { graphql } from 'gatsby';
import {
  Box,
  Heading,
  Text,
  Link,
  Container,
  PreFooterCta,
  PreFooterCtaEntity
} from '@lucid/components-react';
import ReactHtmlParser from 'react-html-parser';
import { getI18nData, getProcessedValue, getEntityFields, isBrowser } from '@lucid/utils';
import {
  Drupal_FieldNodeFlexipageFieldFlexiLinksButtons,
  Drupal_FieldNodeFlexipageFieldFlexiParagraphs,
  Drupal_NodeFlexipage,
  Drupal_NodeNews,
  Drupal_Paragraph,
  Drupal_ParagraphFlexiLinkButton,
  Drupal_ParagraphCarouselSection,
  Drupal_ParagraphCarouselItem
} from 'generated/drupal-graphql';
import { LucidTheme, mqMin } from '@lucid/theme';

import Carousel from 'components/carousel/carousel';
import FlexiBoardMembers from 'components/flexi/flexi-board-members/flexi-board-members';
import FlexiCustomerLogos from 'components/flexi/flexi-customer-logos/flexi-customer-logos';
import FlexiImageSection from 'components/flexi/flexi-image-section/flexi-image-section';
import FlexiLeadership from 'components/flexi/flexi-leadership/flexi-leadership';
import FlexiLocationsSection from 'components/flexi/flexi-locations-section/flexi-locations-section';
import FlexiNewsroomPreviewSection from 'components/flexi/flexi-newsroom-preview-section/flexi-newsroom-preview-section';
import FlexiSocialProof from 'components/flexi/flexi-social-proof/flexi-social-proof';
import FlexiStyledMultiSections from 'components/flexi/flexi-styled-multi-sections/flexi-styled-multi-sections';
import FlexiStyledSection from 'components/flexi/flexi-styled-section/flexi-styled-section';
import FlexiTestimonials from 'components/flexi/flexi-testimonials/flexi-testimonials';
import FlexiFormattedSectionList from 'components/flexi/flexi-formatted-section-list/flexi-formatted-section-list';
import FlexiTimelineSection from 'components/flexi/flexi-timeline-section/flexi-timeline-section';
import FlexiJobs from 'components/flexi/flexi-jobs/flexi-jobs';
import FeatureGrid from 'components/feature-grid-section/feature-grid-section';
import { PageTemplateProps } from 'types/types';

const Flexipage = ({ data, pageContext, location }: PageTemplateProps) => {
  const { langPrefix, pageType, nid, shouldHideSsr, i18nKey } = pageContext;

  const { drupal, i18nData } = data;
  const i18n = getI18nData(i18nData, i18nKey);
  const node = drupal.nodeById as Drupal_NodeFlexipage;
  const newsPreview = drupal.nodeQuery?.entities?.map(
    ({ entityTranslation }: any) => entityTranslation
  ) as Drupal_NodeNews[];
  const { entityMetatags, entityTranslations } = getEntityFields(node);

  const componentMapping: Record<string, any> = {
    flexi_board_members: FlexiBoardMembers,
    flexi_customer_logos: FlexiCustomerLogos,
    flexi_leaders: FlexiLeadership,
    flexi_locations_section: FlexiLocationsSection,
    flexi_newsroom_preview_section: FlexiNewsroomPreviewSection,
    flexi_social_proof: FlexiSocialProof,
    flexi_styled_multi_sections: FlexiStyledMultiSections,
    flexi_styled_section: FlexiStyledSection,
    flexi_testimonials: FlexiTestimonials,
    image_section: FlexiImageSection,
    flexi_formatted_section_list: FlexiFormattedSectionList,
    flexi_timeline_section: FlexiTimelineSection,
    flexi_jobs: FlexiJobs,
    carousel_section: Carousel,
    feature_grid_section: FeatureGrid
  };

  const {
    fieldLinkButtonGroupTitle,
    fieldFlexiParagraphs,
    fieldFlexiLinksButtons,
    fieldShouldHideNavbar = false
  } = node;

  const flexiParagraphs = fieldFlexiParagraphs as Drupal_FieldNodeFlexipageFieldFlexiParagraphs[];
  const flexiButtons = fieldFlexiLinksButtons as Drupal_FieldNodeFlexipageFieldFlexiLinksButtons[];

  let buttonOnClick: () => void;

  return (
    <Layout
      entityTranslations={entityTranslations}
      hasNavbar={!fieldShouldHideNavbar}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      shouldHideSsr={shouldHideSsr}
    >
      <Container
        as="main"
        css={(theme: LucidTheme) => ({
          background: theme.colors.white,
          overflow: 'hidden'
        })}
        extraPadding={3}
      >
        <Box
          display="grid"
          gridColumnGap="20px"
          gridTemplateColumns={{
            default: 'repeat(4, 1fr)',
            tablet: 'repeat(12, 1fr)'
          }}
        >
          <Box
            gridColumn={{ default: '1 / -1' }}
            maxWidth={{ default: '100%', tablet: '780px' }}
            mb={fieldLinkButtonGroupTitle ? 10 : 4}
            mt={8}
            mx="auto"
          >
            {node.fieldFlexiPageSubheader && (
              <Heading
                as="h2"
                dataElementPageLocation="hero"
                m={0}
                mb={3}
                textAlign="center"
                variant="headline2XL"
              >
                {ReactHtmlParser(
                  getProcessedValue(node.fieldFlexiPageSubheader)
                )}
              </Heading>
            )}
            <Heading
              as="h1"
              css={(theme: LucidTheme) => ({
                color: theme.colors.blue[60],
                '> u': {
                  display: 'inline-block',
                  position: 'relative',
                  textDecoration: 'none',
                  zIndex: theme.zIndices[1],
                  '&::before': {
                    content: '""',
                    background: theme.colors.coolGrey[10],
                    position: 'absolute',
                    height: '50%',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: theme.zIndices[0]
                  }
                }
              })}
              dataElementPageLocation="hero"
              m={0}
              mb={6}
              textAlign="center"
              variant="headline3XL"
            >
              {ReactHtmlParser(getProcessedValue(node.fieldFlexiPageHeader))}
            </Heading>
            {node.fieldFlexiPageParagraph && (
              <Box
                css={{
                  textAlign: 'center',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  [mqMin.default]: {
                    maxWidth: '100%'
                  },
                  [mqMin.tablet]: {
                    maxWidth: '580px'
                  }
                }}
              >
                <Text
                  as="div"
                  css={(theme: LucidTheme) => ({
                    color: theme.colors.coolGrey[100],
                    marginBottom: theme.space[6]
                  })}
                  variant="paragraphM300"
                >
                  {ReactHtmlParser(
                    getProcessedValue(node.fieldFlexiPageParagraph)
                  )}
                </Text>
                {fieldLinkButtonGroupTitle && (
                  <Heading
                    as="h2"
                    css={(theme: LucidTheme) => ({
                      margin: theme.space.s8
                    })}
                    variant="headlineM"
                  >
                    {fieldLinkButtonGroupTitle}
                  </Heading>
                )}
                <Box
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: '0 auto',
                    maxWidth: '220px',
                    [mqMin.tabletSmall]: {
                      flexDirection: 'row'
                    }
                  }}
                  display="flex"
                  justifyContent="center"
                >
                  {flexiButtons?.map(({ entity }) => {
                    const {
                      fieldAnchorLink,
                      fieldLinkButtonLink,
                      fieldLinkButtonText,
                      fieldOpenInNewTab,
                      fieldUseButton,
                      fieldUsePrimary
                    } = entity as Drupal_ParagraphFlexiLinkButton;
                    let destination = fieldLinkButtonLink?.url?.path || '';
                    if (fieldAnchorLink) {
                      destination = `#${fieldAnchorLink}`;
                      buttonOnClick = () => {
                        // Just incase a malformed destination is passed in
                        try {
                          if (isBrowser()) {
                            document.querySelector(destination)?.scrollIntoView({ behavior: 'smooth' });
                          }
                        } catch(err) {
                          console.error('Flexipage Button Error: ', err);
                        }
                      };
                    }

                    return (
                      <Link
                        key={fieldLinkButtonText}
                        asButton={!!fieldUseButton && !!fieldAnchorLink}
                        css={(theme: LucidTheme) => ({
                          marginRight: theme.space.s16,
                          marginLeft: theme.space.s16,
                          fontWeight: theme.fontWeights.subheading
                        })}
                        displayAsButton={!!fieldUseButton || !!fieldAnchorLink}
                        newTab={!!fieldOpenInNewTab}
                        onClick={fieldAnchorLink ? buttonOnClick : undefined}
                        to={fieldAnchorLink ? undefined : destination}
                        variant={fieldUsePrimary ? 'primary' : 'secondary'}
                      >
                        {fieldLinkButtonText}
                      </Link>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
          {flexiParagraphs.map(({ entity }) => {
            const { entityBundle, entityId } = entity as Drupal_Paragraph;
            const FlexiComponent = componentMapping[entityBundle as string];
            const newsroomPreviewSection = entityBundle === 'flexi_newsroom_preview_section' ? newsPreview.filter(Boolean) : [];
            if (entityBundle === 'flexi_leaders' && FlexiComponent) {
              return (
                <FlexiComponent
                  key={entityId}
                  entity={entity}
                  showLess={i18n('mktg_show_less')}
                  showMore={i18n('mktg_show_more')}
                />
              );
            }

            if (entityBundle === 'carousel_section' && FlexiComponent) {
              const carouselEntity = entity as Drupal_ParagraphCarouselSection;
              const items = carouselEntity.fieldItems?.map(
                i => i?.entity
              ) as Drupal_ParagraphCarouselItem[];

              return (
                <Carousel overflowMobileImages controlsPosition="bottom" items={items} />
              );
            }

            return FlexiComponent ? (
              <FlexiComponent
                key={entityId}
                entity={entity}
                newsPreview={newsroomPreviewSection}
              />
            ) : null;
          })}
        </Box>
      </Container>
      {node.fieldFlexiPrefooterHeading && node.fieldFlexiPrefooterLink && (
        <PreFooterCta
          hasExtraPadding
          ctas={[{
            fieldButtonText: node.fieldFlexiPrefooterLink?.title,
            fieldButtonLink: node.fieldFlexiPrefooterLink?.url?.path
          }] as PreFooterCtaEntity[]}
          header={node.fieldFlexiPrefooterHeading}
          variant="secondary"
        />
      )}
    </Layout>
  );
};

export default Flexipage;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_show_more
            mktg_show_less
          }
        }
      }
    }
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeFlexipage {
          ...entityFields
          fieldFlexiPageHeader {
            processed
          }
          fieldFlexiPageSubheader {
            processed
          }
          fieldFlexiPageParagraph {
            processed
          }
          fieldLinkButtonGroupTitle
          fieldFlexiLinksButtons {
            entity {
              ... on Drupal_ParagraphFlexiLinkButton {
                fieldAnchorLink
                fieldLinkButtonLink {
                  url {
                    path
                  }
                  title
                }
                fieldLinkButtonText
                fieldOpenInNewTab
                fieldUseButton
                fieldUsePrimary
              }
            }
          }
          fieldFlexiPrefooterHeading
          fieldFlexiPrefooterLink {
            url {
              path
            }
            title
          }
          fieldFlexiParagraphs {
            entity {
              entityId
              entityBundle
              ...flexiBoardMembers
              ...flexiCustomerLogosSection
              ...flexiImageSection
              ...flexiLeadership
              ...flexiLocationsSection
              ...flexiNewsroomPreviewSection
              ...flexiSocialProof
              ...flexiStyledMultiSections
              ...flexiStyledSection
              ...flexiTestimonialSection
              ...flexiFormattedSectionList
              ...flexiTimelineSection
              ...flexiJobs
              ...carouselSection
              ...featureGridSection
            }
          }
          fieldShouldHideNavbar
        }
      }
      nodeQuery(
        sort: [{ field: "field_date.value", direction: DESC }]
        limit: 3
        filter: {
          conditions: [
            { field: "type", value: "news", operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
          ]
        }
      ) {
        entities {
          ... on Drupal_Node {
            entityTranslation(language: $LanguageId) {
              ... on Drupal_NodeNews {
                nid
                title
                fieldDate {
                  value
                }
                fieldImageLink
                fieldPrProduct
                fieldPublisher
                fieldSourceUrl
              }
            }
          }
        }
      }
    }
  }
`;
